import { useEffect, useState } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { organizationsApi } from '../../services/api';
import { useOrganizationStore } from '../../stores/organization';
import toast from 'react-hot-toast';
import { LoadingSpinner } from '../common';

// ---------------------------------------------------------------------------
// Types (mirror backend payment-config response)
// ---------------------------------------------------------------------------

interface StripeConfig {
  publishable_key: string;
  secret_key_set: boolean;
  secret_key_preview: string;
  webhook_secret_set?: boolean;
}

interface RevolutConfig {
  merchant_public_id: string;
  api_key_set: boolean;
  api_key_preview: string;
}

interface PaymentConfigResponse {
  active_gateways: ('stripe' | 'revolut')[];
  stripe: StripeConfig;
  revolut: RevolutConfig;
}

// ---------------------------------------------------------------------------
// UI helpers
// ---------------------------------------------------------------------------

const inputClass = 'w-full rounded-lg border-gray-300 shadow-sm text-sm focus:ring-vida-500 focus:border-vida-500';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1';

function SecretInput({
  label,
  value,
  onChange,
  isSet,
  preview,
  placeholder,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  isSet: boolean;
  preview: string;
  placeholder?: string;
}) {
  const [reveal, setReveal] = useState(false);
  const [editing, setEditing] = useState(false);

  if (isSet && !editing) {
    return (
      <div>
        <label className={labelClass}>{label}</label>
        <div className="flex items-center gap-2">
          <input type="text" value={preview} disabled className={`${inputClass} bg-gray-50 text-gray-500 font-mono`} />
          <button
            type="button"
            onClick={() => { setEditing(true); onChange(''); }}
            className="px-3 py-2 text-xs font-medium text-vida-600 border border-vida-300 rounded-lg hover:bg-vida-50"
          >
            Cambiar
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-1">Clave actual oculta. Pulsa "Cambiar" para introducir un nuevo valor.</p>
      </div>
    );
  }

  return (
    <div>
      <label className={labelClass}>{label}</label>
      <div className="flex items-center gap-2">
        <input
          type={reveal ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`${inputClass} font-mono`}
          placeholder={placeholder}
          autoComplete="off"
        />
        <button
          type="button"
          onClick={() => setReveal(!reveal)}
          aria-label={reveal ? 'Ocultar' : 'Mostrar'}
          className="p-2 text-gray-400 hover:text-gray-600"
        >
          {reveal ? <EyeSlashIcon className="w-5 h-5" /> : <EyeIcon className="w-5 h-5" />}
        </button>
      </div>
      <p className="text-xs text-gray-500 mt-1">Se cifra con AES-256-GCM antes de guardarse en la base de datos.</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function PaymentGatewaysEditor() {
  const selectedOrgId = useOrganizationStore((s) => s.selectedOrg?._id);
  const selectedOrgSlug = useOrganizationStore((s) => s.selectedOrg?.slug);

  const [config, setConfig] = useState<PaymentConfigResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Form state — reflects what the user is currently editing.
  const [stripeActive, setStripeActive] = useState(false);
  const [stripePublishable, setStripePublishable] = useState('');
  const [stripeSecret, setStripeSecret] = useState('');
  const [stripeWebhookSecret, setStripeWebhookSecret] = useState('');

  const [revolutActive, setRevolutActive] = useState(false);
  const [revolutPublicId, setRevolutPublicId] = useState('');
  const [revolutApiKey, setRevolutApiKey] = useState('');

  useEffect(() => {
    if (!selectedOrgId) return;
    void loadConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgId]);

  const loadConfig = async () => {
    if (!selectedOrgId) return;
    setLoading(true);
    try {
      const res = await organizationsApi.getPaymentConfig(selectedOrgId);
      const data: PaymentConfigResponse = res.data?.data;
      setConfig(data);
      setStripeActive(data.active_gateways.includes('stripe'));
      setRevolutActive(data.active_gateways.includes('revolut'));
      setStripePublishable(data.stripe.publishable_key || '');
      setRevolutPublicId(data.revolut.merchant_public_id || '');
      setStripeSecret('');
      setStripeWebhookSecret('');
      setRevolutApiKey('');
      setDirty(false);
    } catch (e) {
      console.error('Failed to load payment config', e);
      toast.error('Error al cargar la configuración de pagos');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!selectedOrgId) return;
    setSaving(true);
    try {
      const active: string[] = [];
      if (stripeActive) active.push('stripe');
      if (revolutActive) active.push('revolut');

      const body: any = {
        active_gateways: active,
        stripe: {
          publishable_key: stripePublishable,
        },
        revolut: {
          merchant_public_id: revolutPublicId,
        },
      };
      if (stripeSecret) body.stripe.secret_key = stripeSecret;
      if (stripeWebhookSecret) body.stripe.webhook_secret = stripeWebhookSecret;
      if (revolutApiKey) body.revolut.api_key = revolutApiKey;

      await organizationsApi.savePaymentConfig(selectedOrgId, body);
      toast.success('Configuración de pagos guardada');
      // Re-fetch to refresh masked previews.
      await loadConfig();
    } catch (e: any) {
      console.error('Failed to save payment config', e);
      toast.error(e?.response?.data?.error || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const markDirty = () => setDirty(true);
  // Configuración automática del webhook en Stripe con la clave secreta guardada.
  const [webhookBusy, setWebhookBusy] = useState(false);
  const setupWebhook = async () => {
    if (!selectedOrgId) return;
    setWebhookBusy(true);
    try {
      const res = await organizationsApi.setupStripeWebhook(selectedOrgId);
      const status = res.data.data.status;
      toast.success(status === 'updated' ? 'Webhook de Stripe actualizado' : 'Webhook creado en Stripe y secreto guardado');
      await loadConfig();
    } catch (error: unknown) {
      const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(message || 'No se pudo configurar el webhook en Stripe');
    } finally {
      setWebhookBusy(false);
    }
  };

  const webhookUrl = `${window.location.origin}/api/public/ecommerce/${selectedOrgSlug || '<slug>'}/stripe/webhook`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-400">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!selectedOrgId) {
    return <div className="p-8 text-center text-gray-500">Selecciona una organización para configurar las pasarelas.</div>;
  }

  if (!config) {
    return <div className="p-8 text-center text-gray-500">No se pudo cargar la configuración.</div>;
  }

  return (
    <div className="space-y-4">
      {/* Save bar */}
      <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 px-6 py-3 flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Pasarelas de pago</h2>
          <p className="text-sm text-gray-500">Configura las claves de Stripe y Revolut Pay para el checkout de esta organización</p>
        </div>
        <div className="flex items-center gap-3">
          {dirty && <span className="text-xs text-amber-600 font-medium">Cambios sin guardar</span>}
          <button
            onClick={handleSave}
            disabled={saving || !dirty}
            className="inline-flex items-center gap-2 px-4 py-2 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving && <LoadingSpinner size="sm" />}
            Guardar
          </button>
        </div>
      </div>

      {/* Stripe */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Stripe</h3>
            <p className="text-xs text-gray-500 mt-0.5">Tarjetas de crédito/débito · cuenta propia por organización</p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={stripeActive}
              onChange={(e) => { setStripeActive(e.target.checked); markDirty(); }}
              className="rounded text-vida-600 focus:ring-vida-500"
            />
            <span className={`text-xs font-semibold uppercase tracking-wider ${stripeActive ? 'text-green-700' : 'text-gray-400'}`}>
              {stripeActive ? 'Activa' : 'Inactiva'}
            </span>
          </label>
        </div>

        <div>
          <label className={labelClass}>Publishable Key (pk_...)</label>
          <input
            type="text"
            name="stripe-publishable-key"
            value={stripePublishable}
            onChange={(e) => { setStripePublishable(e.target.value); markDirty(); }}
            className={`${inputClass} font-mono`}
            placeholder="pk_live_... o pk_test_..."
            // Browsers love to autofill anything text-y with the user email.
            // Force them off so we don't end up persisting "user@example.com"
            // as a Stripe key when the user mistakenly accepts an autofill.
            autoComplete="off"
            data-form-type="other"
            data-1p-ignore
            data-lpignore="true"
          />
          <p className="text-xs text-gray-500 mt-1">Pública: se sirve al frontend. No es secreta.</p>
        </div>

        <SecretInput
          label="Secret Key (sk_...)"
          value={stripeSecret}
          onChange={(v) => { setStripeSecret(v); markDirty(); }}
          isSet={config.stripe.secret_key_set && !stripeSecret}
          preview={config.stripe.secret_key_preview}
          placeholder="sk_live_... o sk_test_..."
        />

        {/* Webhook (VIDA-348): sin él los pedidos nunca pasan a «pagado». */}
        <div className="border-t border-gray-100 pt-4 space-y-3">
          <div>
            <h4 className="text-sm font-semibold text-gray-900">Webhook de confirmación de pagos</h4>
            <p className="text-xs text-gray-500 mt-0.5">
              Con la clave secreta ya guardada, pulsa «Configurar en Stripe»: crea el endpoint con todos los eventos y
              guarda su secreto de firma. Si prefieres hacerlo a mano, en Stripe → Desarrolladores → Webhooks añade esta
              URL con los eventos <code className="font-mono">payment_intent.succeeded</code>,{' '}
              <code className="font-mono">payment_intent.payment_failed</code>,{' '}
              <code className="font-mono">payment_intent.canceled</code> y{' '}
              <code className="font-mono">charge.refunded</code>, y pega aquí su secreto de firma.
            </p>
          </div>
          <div>
            <button
              type="button"
              onClick={() => void setupWebhook()}
              disabled={webhookBusy || !config.stripe.secret_key_set || dirty}
              className="px-3 py-2 text-sm font-medium text-white bg-vida-600 rounded-lg hover:bg-vida-700 disabled:opacity-50"
            >
              {webhookBusy ? 'Configurando…' : config.stripe.webhook_secret_set ? 'Revisar webhook en Stripe' : 'Configurar en Stripe'}
            </button>
            {!config.stripe.secret_key_set && (
              <p className="text-xs text-gray-500 mt-1">Guarda primero la clave secreta de Stripe.</p>
            )}
            {config.stripe.secret_key_set && dirty && (
              <p className="text-xs text-gray-500 mt-1">Guarda los cambios antes de configurar el webhook.</p>
            )}
          </div>
          <div>
            <label htmlFor="stripe-webhook-url" className={labelClass}>URL del webhook</label>
            <div className="flex items-center gap-2">
              <input id="stripe-webhook-url" type="text" readOnly value={webhookUrl} className={`${inputClass} bg-gray-50 font-mono text-xs`} />
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard
                    .writeText(webhookUrl)
                    .then(() => toast.success('URL copiada'))
                    .catch(() => toast.error('No se pudo copiar'));
                }}
                className="px-3 py-2 text-xs font-medium text-vida-600 border border-vida-300 rounded-lg hover:bg-vida-50"
              >
                Copiar
              </button>
            </div>
          </div>
          <SecretInput
            label="Secreto de firma del webhook (whsec_...)"
            value={stripeWebhookSecret}
            onChange={(v) => { setStripeWebhookSecret(v); markDirty(); }}
            isSet={!!config.stripe.webhook_secret_set && !stripeWebhookSecret}
            preview="••••••••"
            placeholder="whsec_..."
          />
          {!config.stripe.webhook_secret_set && stripeActive && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Sin el webhook los pedidos pagados se quedan como «pendientes».
            </p>
          )}
        </div>
      </div>

      {/* Revolut Pay */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Revolut Pay</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Almacenamiento de credenciales — la integración real del flujo de pago llega en una próxima fase (B.3).
            </p>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={revolutActive}
              onChange={(e) => { setRevolutActive(e.target.checked); markDirty(); }}
              className="rounded text-vida-600 focus:ring-vida-500"
            />
            <span className={`text-xs font-semibold uppercase tracking-wider ${revolutActive ? 'text-green-700' : 'text-gray-400'}`}>
              {revolutActive ? 'Activa' : 'Inactiva'}
            </span>
          </label>
        </div>

        <div>
          <label className={labelClass}>Merchant Public ID</label>
          <input
            type="text"
            name="revolut-merchant-public-id"
            value={revolutPublicId}
            onChange={(e) => { setRevolutPublicId(e.target.value); markDirty(); }}
            className={`${inputClass} font-mono`}
            placeholder="merchant_..."
            autoComplete="off"
            data-form-type="other"
            data-1p-ignore
            data-lpignore="true"
          />
        </div>

        <SecretInput
          label="API Key"
          value={revolutApiKey}
          onChange={(v) => { setRevolutApiKey(v); markDirty(); }}
          isSet={config.revolut.api_key_set && !revolutApiKey}
          preview={config.revolut.api_key_preview}
          placeholder="sk_..."
        />
      </div>

      {dirty && (
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-vida-600 text-white text-sm font-medium rounded-lg hover:bg-vida-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving && <LoadingSpinner size="sm" />}
            Guardar cambios
          </button>
        </div>
      )}
    </div>
  );
}
